'use client';

import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import { useQuality } from '@/components/shared/QualityProvider';
import { getLenis } from '@/lib/lenisInstance';

// End-of-page CTA pair that closes both /` and /grid:
//   • "Voltar ao início" — Lenis-smoothed scroll back to top.
//   • Context switch — "Ver em 3D" on /grid, "Ver de forma mais simples"
//     on /. Hidden on low tier where switching has nowhere to go
//     (mobile-only renders the grid feed on both routes anyway).
//
// Sits visually after the InfoSection's contact chapter so the user
// has one last decision: leave (back to top → contact via the chapter
// CTA), or change perspective on the same content.
export function PageOutro() {
  const t = useTranslations('PageOutro');
  const pathname = usePathname();
  const { tier } = useQuality();

  const onGrid = pathname.startsWith('/grid');
  const switchTarget = onGrid ? '/' : '/grid';
  const switchLabel = onGrid
    ? t('switchPromptToJourney')
    : t('switchPromptToGrid');

  // Low tier has no journey, so the /grid ⇄ / toggle is meaningless
  // (both render the grid feed). 'pending' is the initial tier before
  // the QualityProvider resolves — hide until we know.
  const showSwitch = tier !== 'low' && tier !== 'pending';

  const scrollToTop = () => {
    const lenis = getLenis();
    if (lenis) {
      // Match HIJACK.sidebar travel pacing — a guided pull rather than a
      // snap. Longer than the default Lenis duration so it doesn't feel
      // like a jump cut from the end of the page back to the hero.
      lenis.scrollTo(0, { duration: 1.6 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <section
      aria-label="Page outro"
      className="mx-auto flex w-full max-w-5xl flex-col items-center justify-center gap-12 px-6 py-24 sm:py-32 md:flex-row md:gap-24"
    >
      <button
        type="button"
        onClick={scrollToTop}
        className="group/cta inline-flex flex-col items-start gap-2 py-1"
      >
        <span className="flex items-center gap-3 font-mono text-base uppercase tracking-[5px] text-(--color-muted) transition-colors duration-300 ease-out group-hover/cta:text-(--color-accent) sm:gap-4 sm:text-lg sm:tracking-[6px]">
          {t('backToTop')}
          <span
            aria-hidden
            className="text-xl leading-none text-(--color-muted)/70 transition-[transform,color] duration-500 ease-out group-hover/cta:-translate-y-0.5 group-hover/cta:text-(--color-accent) sm:text-2xl"
          >
            ↑
          </span>
        </span>
        <span
          aria-hidden
          className="block h-px w-full origin-left scale-x-0 bg-(--color-accent) transition-transform duration-500 ease-out group-hover/cta:scale-x-100"
        />
      </button>

      {showSwitch && (
        <Link
          href={switchTarget}
          className="group/cta inline-flex flex-col items-start gap-2 py-1"
        >
          <span className="flex items-center gap-3 font-mono text-base uppercase tracking-[5px] text-(--color-muted) transition-colors duration-300 ease-out group-hover/cta:text-(--color-accent) sm:gap-4 sm:text-lg sm:tracking-[6px]">
            {switchLabel}
            <span
              aria-hidden
              className="text-xl leading-none text-(--color-muted)/70 transition-[transform,color] duration-500 ease-out group-hover/cta:translate-x-1.5 group-hover/cta:text-(--color-accent) sm:text-2xl"
            >
              →
            </span>
          </span>
          <span
            aria-hidden
            className="block h-px w-full origin-left scale-x-0 bg-(--color-accent) transition-transform duration-500 ease-out group-hover/cta:scale-x-100"
          />
        </Link>
      )}
    </section>
  );
}
